/**
 * ATL-2021 — the Firestore value for a date the TYPED API CLIENT delivered.
 *
 * In Supabase read mode a record reaches the screens through `deserializeDates`
 * (packages/api-client `toTimestampLike`), which turns every nested `DATE_FIELDS` key holding an ISO
 * string — `cartItems[].createdAt`, `updatedAt`, `saleDate`, … — into a plain object
 * `{ toDate: () => Date, seconds, nanoseconds }`. That object is a READ model. A writer that puts it
 * back into Firestore unchanged fails in two different ways, depending on the SDK:
 *
 *   - web (`firebase/compat`) rejects the whole write — "Function WriteBatch.update() called with
 *     invalid data. Unsupported field value: a function" — which is what stopped every web return of
 *     a post-cutover sale (ИП Смак, sale ZluwkenY3zghJyt8Jtyk, 2026-11-02);
 *   - native (`@react-native-firebase`) does NOT throw: its serializer warns "Unknown data type" for
 *     the function and writes the object as a MAP `{ seconds, nanoseconds, toDate: null }`, so a
 *     `Timestamp` field silently becomes a map in the write master.
 *
 * This converts exactly that shape — a PLAIN object whose `toDate` is a function — to the `Date`
 * it stands for, which both SDKs store as a real Firestore `Timestamp`. Everything else is returned
 * as it is: SDK `Timestamp`s, `DocumentReference`s (`cartItems[].cartItemRef`), `FieldValue`
 * sentinels and `Date`s are class instances, not plain objects, so they are neither recursed into
 * nor rewritten. In particular this is NOT a sanitizer: an unrelated function value is left in
 * place so the SDK still refuses it loudly, instead of being dropped and written as missing data.
 *
 * A `toDate()` that yields no valid date is refused with an error naming the field: writing an
 * Invalid Date (or `null`) would replace a real timestamp in someone's books with garbage.
 * `toTimestampLike` never builds such a value (ATL-1313 returns the string instead), so this only
 * fires for a shape nobody has seen yet.
 */

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

const isTimestampLike = (value: Record<string, unknown>): value is Record<string, unknown> & { toDate: () => unknown } =>
  typeof value.toDate === 'function'

const convert = (value: unknown, path: string, seen: WeakSet<object>): unknown => {
  if (Array.isArray(value)) {
    if (seen.has(value)) throw new Error(`timestampLikeToFirestore: cycle at ${path}`)
    seen.add(value)
    const out = value.map((item, index) => convert(item, `${path}[${index}]`, seen))
    seen.delete(value)
    return out
  }
  if (!isPlainObject(value)) return value

  if (isTimestampLike(value)) {
    const date = value.toDate()
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      throw new Error(`timestampLikeToFirestore: ${path} is a Timestamp-like value whose toDate() is not a valid date`)
    }
    return date
  }

  if (seen.has(value)) throw new Error(`timestampLikeToFirestore: cycle at ${path}`)
  seen.add(value)
  const out: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(value)) {
    out[key] = convert(child, path ? `${path}.${key}` : key, seen)
  }
  seen.delete(value)
  return out
}

/** A copy of `value` with every typed-API Timestamp-like object replaced by its `Date`. */
export const timestampLikeToFirestore = <T>(value: T): T => convert(value, '', new WeakSet()) as T
